import { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { i18n } from '../common/localization';
import PropTypes from 'prop-types';
import { styles } from '../common/styles';
import { ConfirmationModal } from '../common/ConfirmationModal';
import { InfoModal } from '../common/InfoModal';
import DeleteIcon from '../icons/DeleteIcon';
import BookingIcon from '../icons/BookingIcon';

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [hh, mm] = timeStr.split(":");
  return `${hh}:${mm}`;
}

export const BookingListBox = ({
  isBooking,
  data,
  title,
  actionLabel,
  onItemAction,
  isRestaurant = false
}) => {
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [infoVisible, setInfoVisible]       = useState(false);
  const [infoText, setInfoText]             = useState('');
  const [stateError, setStateError]         = useState(false);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [loading, setLoading]               = useState(false);

  const handlePress = (item) => {
    setSelectedItem(item);
    setConfirmVisible(true);
  };

  const confirmAction = async (notes) => {
    setConfirmVisible(false);
    setLoading(true);
    try {
      await onItemAction(selectedItem, notes);
      setInfoText(i18n.t(isBooking ? 'bookingSuccess' : 'deleteSuccess'));
      setStateError(false);
      setInfoVisible(true);
    } catch (e) {
      setInfoText(i18n.t(isBooking ? 'bookingFail' : 'deleteFail'));
      setStateError(true);
      setInfoVisible(true);
    } finally {
      setLoading(false);
    }
  };

  const renderItem = ({ item }) => (
    <View style={styles.reservationItemContainer}>
      <View style={styles.reservationItemInfo}>
        <Text style={styles.reservationItemTitle}>
        { isBooking ?
            formatTime(item.StartTime) + " - " + formatTime(item.EndTime)
          :
            new Date(item.Start * 1000).toLocaleDateString(i18n.locale)
        }
        </Text>
        <Text style={styles.reservationItemTime}>
        { isBooking ?
            item.MaxPartecipants - item.TotalBookings + " " + i18n.t("availableSpots")
          :
            formatTime(item.Lesson.StartTime) + " - " + formatTime(item.Lesson.EndTime)
        }
        </Text>
      </View>
      <Pressable
        onPress={() => handlePress(item)}
        style={{ marginRight: 8 }}
        disabled={loading}
      >
        { isBooking ? <BookingIcon/> : <DeleteIcon/> }
      </Pressable>
    </View>
  );

  return (
    <View style={styles.reservationsBox}>
      <Text style={styles.reservationsBoxTitle}>{title}</Text>
      {data.length === 0 ? (
        <Text style={styles.noReservationsText}>{isBooking ? i18n.t("noAvailableSlots") : i18n.t("noSlotsBooked")}</Text>
      ) : (
        data.map((item, index) => (
          <View
            key={item.ID?.toString() || `${item.LessonID}-${index}`}
          >
            {renderItem({ item })}
          </View>
        ))
      )}

      <ConfirmationModal
        visible={confirmVisible}
        text={i18n.t("confirmAction", { action: isBooking ? i18n.t("confirm").toLowerCase() : i18n.t("delete").toLowerCase() })}
        okText={actionLabel}
        onPressOk={confirmAction}
        onPressClose={() => setConfirmVisible(false)}
        isRestaurant={isRestaurant}
      />

      <InfoModal
        visible={infoVisible}
        onPressClose={() => setInfoVisible(false)}
        text={infoText}
        isError={stateError}
      />
    </View>
  );
};

BookingListBox.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
  actionLabel: PropTypes.string.isRequired,
  onItemAction: PropTypes.func.isRequired,
  isRestaurant: PropTypes.bool,
};
